import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { CommentSelector } from 'types/graphql-global-types';
import { Logger } from '@utils/logger';
import { GET_UPDATED_COMMENTS, GET_UPDATED_COMMENTS_BY_ID } from '@queries/common';
import {
    getUpdatedCommentsById as getUpdatedCommentsByIdReturnType,
    getUpdatedCommentsByIdVariables,
    getUpdatedComments as getUpdatedCommentsReturnType,
    getUpdatedCommentsVariables,
} from '@queries/generated/common.generated';

const useCommentQueries = (parentId: string | undefined, entityType: string) => {
    const apolloClient = useApolloClient();

    const abortControllersRef = useRef<AbortController[]>([]);

    const getAbortController = () => {
        const abortController = new AbortController();
        abortControllersRef.current.push(abortController);

        setTimeout(() => {
            abortController.abort('Timeout');
        }, 60000);

        return abortController;
    };

    const removeAbortController = (abortController: AbortController) => {
        abortControllersRef.current = abortControllersRef.current.filter(
            (controller) => controller !== abortController,
        );
    };

    const getUpdatedComments = async (commentSelectors: CommentSelector[]) => {
        const abortController = getAbortController();
        try {
            const result = await apolloClient.query<getUpdatedCommentsReturnType, getUpdatedCommentsVariables>({
                query: GET_UPDATED_COMMENTS,
                variables: {
                    updatedCommentsInput: {
                        parentId,
                        entityType,
                        commentSelectors,
                    },
                },
                fetchPolicy: 'no-cache',
                context: {
                    fetchOptions: { signal: abortController.signal },
                },
            });

            if (result.data?.updatedComments) {
                return result.data.updatedComments;
            }
        } catch (error) {
            Logger.error('error when getting updated comments', error);
        } finally {
            removeAbortController(abortController);
        }

        return null;
    };

    const getUpdatedCommentsById = async (commentIds: string[], commentSelectors: CommentSelector[]) => {
        const abortController = getAbortController();
        try {
            const result = await apolloClient.query<getUpdatedCommentsByIdReturnType, getUpdatedCommentsByIdVariables>({
                query: GET_UPDATED_COMMENTS_BY_ID,
                variables: {
                    updatedCommentsByIdInput: {
                        parentId,
                        entityType,
                        commentIds,
                        commentSelectors,
                    },
                },
                fetchPolicy: 'no-cache',
                context: {
                    fetchOptions: { signal: abortController.signal },
                },
            });

            if (result.data?.updatedCommentsById) {
                return result.data.updatedCommentsById;
            }
        } catch (error) {
            Logger.error('error when getting updated comments by id', error);
        } finally {
            removeAbortController(abortController);
        }

        return null;
    };

    useEffect(() => {
        return () => {
            abortControllersRef.current.forEach((controller) => controller.abort('Unmount'));
        };
    }, []);

    return {
        getUpdatedComments,
        getUpdatedCommentsById,
    };
};

export default useCommentQueries;
